import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";

interface BreadcrumbItem {
  label: string;
  to: string;
}

interface PageBreadcrumbProps {
  title: string; // Title text (e.g., "Add Organisation" or "Edit Organisation")
  titleLink?: string; // Link for the title (defaults to first breadcrumb item's to)
  breadcrumbs?: BreadcrumbItem[]; // Array of breadcrumb items
  backNavigationPath?: string | number; // Path to navigate back to (-1 for browser back, or specific path)
  className?: string; // Additional CSS classes
  isBackArrow?: boolean;
}

const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({
  title,
  titleLink,
  breadcrumbs,
  backNavigationPath = -1,
  className = "",
  isBackArrow = true,
}) => {
  const navigate = useNavigate();
  const breadcrumbItems = breadcrumbs ?? [];
  const fallbackPath = breadcrumbItems[0]?.to || "/";

  const handleBackClick = () => {
    if (typeof backNavigationPath === "number" && backNavigationPath === -1) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(fallbackPath);
      }
    } else if (typeof backNavigationPath === "string") {
      navigate(backNavigationPath);
    } else {
      navigate(fallbackPath);
    }
  };

  const titleLinkPath = titleLink || fallbackPath || "#";

  return (
    <div className={`mb-4 ${className}`}>
      <div className="d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
        {/* Left: Back arrow + Title */}
        <div className="d-flex align-items-center">
          {isBackArrow && (
            <ArrowBack
              className="me-2"
              style={{ fontSize: "1.25rem", cursor: "pointer" }}
              onClick={handleBackClick}
            />
          )}
          <h5
            className="mb-0 fw-bold"
            style={{ fontFamily: "Poppins, sans-serif", fontSize: "17px" }}
          >
            <Link to={titleLinkPath} className="text-decoration-none text-dark">
              {title}
            </Link>
          </h5>
        </div>

        {/* Right: Breadcrumb */}
        <div
          className="text-muted"
          style={{ fontFamily: "Poppins, sans-serif", fontSize: "13px" }}
        >
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && " > "}
              {index === breadcrumbItems.length - 1 ? (
                <span>{item.label}</span>
              ) : (
                <Link to={item.to} className="text-decoration-none text-muted">
                  {item.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PageBreadcrumb;
